import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from "@/components/UI/toaster";
import { store } from './store/store';
import { withLoading } from './components/UI/withLoading';

// Component imports - Make sure all these use ES6 import syntax
import AmountMismatchLogs from './components/LOAMS/InvoiceManagement/InvoiceAmountMismatchLogs'; 
import Login from './components/Auth/Login';
import AccountsDashboard from './components/Accounts/AccountsDashboard';
import Dashboard from './components/Dashboard';
import AccountsTwo from './components/AccountsTwo/AccountsTwo';
import ClientList from '@/components/Client/ClientList';
import BOQDashboard from '@/components/BOQ/BOQDashboard';
import LOAMSDashboard from './components/LOAMS/LOAMSDashboard';
import EngineerDashboard from './components/EngineerDashboard/engineer';
import MISDashboard from './components/MIS/MISDashboard';
import ProfitLoss from './components/MIS/ProfitLoss';
import PLtrend from './components/MIS/PLtrend';

// Wrap the main content with loading HOC
const MainContent = ({ children }) => (
  <>
    <Toaster />
    {children}
  </>
);

const MainContentWithLoading = withLoading(MainContent);

// Protected Route Component with enhanced security
const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  
  try {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
    
    if (!token || !user) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const userRole = user.role?.toLowerCase();

    // Super admin has access to everything
    if (userRole === 'super_admin') {
      return children;
    }

    // Allow loams_viewer to access accounts dashboard and LOAMS
    if (userRole === 'loams_viewer') {
      // Block access to other routes except accounts and loams
      if (!location.pathname.includes('accounts') && !location.pathname.includes('loams')) {
        return <Navigate to="/accounts" replace />;
      }
      return children;
    }

    // For other roles, check if they're allowed
    const normalizedAllowedRoles = allowedRoles.map(role => role.toLowerCase());
    if (!normalizedAllowedRoles.includes(userRole)) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }

    return children;
  } catch (error) {
    console.error('Auth error:', error);
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">Something went wrong</h1>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Page Components
const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-red-600">Unauthorized Access</h1>
      <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
      <button 
        onClick={() => window.history.back()} 
        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
      >
        Go Back
      </button>
    </div>
  </div>
);

const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-900">404 - Page Not Found</h1>
      <p className="mt-2 text-gray-600">The page you're looking for doesn't exist.</p>
      <button 
        onClick={() => window.history.back()} 
        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
      >
        Go Back
      </button>
    </div>
  </div>
);

const AccountsTwoLayout = () => {
  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Accounts Management</h1>
            <div className="text-gray-600">{currentDate}</div>
          </div>
        </div>
        <AccountsTwo />
      </div>
    </div>
  );
};

const BOQRoutes = () => (
  <Routes>
    <Route path="/" element={<BOQDashboard />} />
  </Routes>
);

// MIS Routes Component
const MISRoutes = () => (
  <Routes>
    <Route path="/" element={<MISDashboard />} />
    <Route path="/profit-loss" element={<ProfitLoss />} />
    <Route path="/profit-loss-trend" element={<PLtrend />} />
  </Routes>
);

// LOAMS Routes Component
const LOAMSRoutes = () => (
  <Routes>
    <Route path="/" element={<LOAMSDashboard />} />
    <Route path="/invoice-amount-logs" element={<AmountMismatchLogs />} />
  </Routes>
);

const App = () => {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <BrowserRouter>
          <MainContentWithLoading>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              
              {/* BOQ Routes */}
              <Route path="/boq-dashboard/*" element={
                <ProtectedRoute allowedRoles={['boq_admin', 'admin', 'super_admin']}>
                  <BOQRoutes />
                </ProtectedRoute>
              } />

              {/* Accounts Routes */}
              <Route path="/accounts" element={
                <ProtectedRoute allowedRoles={['accounts', 'admin', 'super_admin','loams_only']}>
                  <AccountsDashboard />
                </ProtectedRoute>
              } />

              {/* LOAMS Routes */}
              <Route path="/loams/*" element={
                <ProtectedRoute allowedRoles={['accounts', 'admin', 'engineer', 'loams_viewer', 'super_admin', 'loams_only']}>
                  <LOAMSRoutes />
                </ProtectedRoute>
              } />

              <Route path="/clients" element={
                <ProtectedRoute allowedRoles={['accounts', 'admin', 'super_admin']}>
                  <ClientList />
                </ProtectedRoute>
              } />

              {/* Engineer Routes */}
              <Route path="/engineer/*" element={
                <ProtectedRoute allowedRoles={['engineer', 'super_admin']}>
                  <EngineerDashboard />
                </ProtectedRoute>
              } />

              <Route path="/accounts1/*" element={
                <ProtectedRoute allowedRoles={['accounts', 'admin', 'super_admin']}>
                  <Dashboard />
                </ProtectedRoute>
              } />

              <Route path="/accounts2/*" element={
                <ProtectedRoute allowedRoles={['accounts', 'admin', 'super_admin']}>
                  <AccountsTwoLayout />
                </ProtectedRoute>
              } />

              {/* MIS Routes */}
              <Route path="/MIS/*" element={
                <ProtectedRoute allowedRoles={['accounts', 'admin', 'super_admin']}>
                  <MISRoutes />
                </ProtectedRoute>
              } />

              {/* Utility Routes */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </MainContentWithLoading>
        </BrowserRouter>
      </ErrorBoundary>
    </Provider>
  );
};

export default App;